/**
 * Varigheder. OnePro lagrer akkumuleret tid som hele sekunder (public.todos.total_time),
 * og timeoversigten viser dem som et ur frem for som decimaltal.
 *
 * Det er bevidst noget andet end formatHours: "02:19:15" er den vaerdi konsulenten
 * genkender fra sin egen timer i OnePro, mens "2,32" foerst giver mening i en beregning.
 * Faktureringen regner videre paa timer med to decimaler - se formatHours.
 */

/**
 * Sekunder som "HH:MM:SS". Timetallet ruller ikke over ved 24: en opgave med 771 timers
 * arbejde vises som "771:36:10", ikke som "32 dage".
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';

  const total = Math.round(seconds);
  // Negativ tid er en datafejl i kilden. Den vises som den er frem for at blive rundet
  // op til nul, saa fejlen kan ses i stedet for at ligne en registrering paa 00:00:00.
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(total);

  const hh = Math.floor(abs / 3600);
  const mm = Math.floor((abs % 3600) / 60);
  const ss = abs % 60;

  return `${sign}${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/**
 * Den tid en igangvaerende timer viser lige nu.
 *
 * OnePro skriver foerst den forloebne tid tilbage til total_time naar timeren stoppes.
 * Indtil da staar der kun det der var akkumuleret foer denne koersel, og start_time siger
 * hvornaar koerslen begyndte. Uden det sidste led ville en koerende opgave staa stille paa
 * listen, selvom uret loeber i OnePro.
 *
 * `now` er en parameter og ikke Date.now(), saa funktionen er ren og kan testes.
 */
export function runningSeconds(
  accumulatedSeconds: number,
  startedAt: string | null | undefined,
  now: Date,
): number {
  if (!startedAt) return accumulatedSeconds;

  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return accumulatedSeconds;

  /**
   * Nedrunding. Tallet her er et *skoen* over noget OnePro ejer, og naar timeren stopper,
   * erstattes det af den vaerdi OnePro har gemt i total_time.
   *
   * Derfor maa skoennet aldrig ligge over den gemte vaerdi: goer det det, falder tallet
   * tilbage i det oejeblik timeren stoppes, og et tal der taeller baglaens ser ud som en
   * fejl - ogsaa naar det er rettelsen der er rigtig. Nedrunding kan i vaerste fald vise
   * ét sekund for lidt et oejeblik; oprunding viser for meget og skylder altid tilbage.
   *
   * Math.max holder uret i ro hvis klienten gaar bagud i forhold til serveren. At staa
   * stille er tydeligere end at taelle den forkerte vej.
   */
  return accumulatedSeconds + Math.max(0, Math.floor((now.getTime() - started) / 1000));
}

/** Sekunder som decimaltimer. Broen fra OnePro's timere til faktureringens timetal. */
export function secondsToHours(seconds: number | null | undefined): number | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
  return Math.round((seconds / 3600) * 100) / 100;
}
